import { api } from './generated-client'
import type { User, LoginCredentials, LoginResponse } from '@/types/auth'

// 後端 /auth/me 回應格式
interface MeResponse {
  userId: number
  tenantId: number
  email: string
  role: string
  platformRoles: string[]
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const { data, error } = await api.POST('/auth/console/login', { body: credentials })
    if (error || !data) throw new Error('Login failed')
    return data as LoginResponse
  },

  async logout(): Promise<void> {
    const { error } = await api.POST('/auth/logout', { body: {} } as never)
    if (error) throw new Error('Logout failed')
  },

  async me(): Promise<User> {
    const { data, error } = await api.GET('/auth/me')
    if (error || !data) throw new Error('Failed to fetch user info')
    const meData = data as MeResponse
    return {
      id: meData.userId,
      email: meData.email,
      name: meData.email.split('@')[0],
      role: meData.role,
      tenantId: meData.tenantId,
      platformRoles: meData.platformRoles ?? [],
    }
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const { error } = await api.PUT('/auth/password', {
      body: { oldPassword, newPassword }
    })
    if (error) throw new Error('Failed to change password')
  },

  /**
   * 回傳 challengeId：確認端要帶回去的範圍鍵。手機簡訊碼**必帶**（每挑戰 5 次錯碼
   * 即作廢）；Email 連結 token 可不帶。帳號不存在時後端也回一個值（不洩漏存在性）。
   */
  async requestPasswordResetByEmail(email: string): Promise<{ challengeId: string; expiresInMinutes: number }> {
    const { data, error } = await api.POST('/auth/password/reset/by-email', {
      body: { email }
    })
    if (error || !data) throw new Error('Failed to request password reset')
    return { challengeId: data.challengeId, expiresInMinutes: data.expiresInMinutes }
  },

  async requestPasswordResetByMobile(mobile: string): Promise<{ challengeId: string; expiresInMinutes: number }> {
    const { data, error } = await api.POST('/auth/password/reset/by-mobile', {
      body: { mobile }
    })
    if (error || !data) throw new Error('Failed to request password reset')
    return { challengeId: data.challengeId, expiresInMinutes: data.expiresInMinutes }
  },

  /**
   * @param challengeId 發送端回傳的挑戰識別碼；簡訊碼必帶，Email token 可省略。
   */
  async confirmPasswordReset(token: string, newPassword: string, challengeId?: string): Promise<void> {
    const { error } = await api.POST('/auth/password/reset/confirm', {
      body: { token, newPassword, challengeId: challengeId ?? null }
    })
    if (error) throw new Error('Failed to confirm password reset')
  },

  async sendVerifyEmailCode(): Promise<void> {
    const { error } = await api.POST('/auth/verify/email/send')
    if (error) throw new Error('Failed to send verification email')
  },

  async confirmVerifyEmail(code: string): Promise<void> {
    const { error } = await api.POST('/auth/verify/email/confirm', {
      body: { code }
    })
    if (error) throw new Error('Failed to verify email')
  },

  async sendVerifyMobileCode(): Promise<void> {
    const { error } = await api.POST('/auth/verify/mobile/send')
    if (error) throw new Error('Failed to send verification SMS')
  },

  async confirmVerifyMobile(code: string): Promise<void> {
    const { error } = await api.POST('/auth/verify/mobile/confirm', {
      body: { code }
    })
    if (error) throw new Error('Failed to verify mobile')
  },
}
